import { useCallback, useEffect, useRef, useState } from 'react';
import {
  collection,
  onSnapshot,
  type Firestore,
} from 'firebase/firestore';
import { useDb } from '../../../theaWeb/firebase/FirebaseContext';
import {
  recipientCollectionPath,
} from '../../../theaWeb/schemas/paths';
import type { Recipient } from '../../../theaWeb/schemas/recipient';
import { resolveRecipientEmoji } from '../../../theaWeb/lib/resolveRecipientEmoji';
import type { DashboardPerson } from './types';

/** Terminal-or-loading status of the recipient listener. Committed ATOMICALLY
 *  (one state update per event) so a consumer keying "confirmed empty" on
 *  `status === 'success'` can never observe success while an error is pending —
 *  the false-empty class this hook's consumer must avoid. */
export type FriendsListStatus = 'loading' | 'success' | 'error';

/** How long the first snapshot may take before the consumer may show a soft
 *  "still loading — try again". Leads ONLY to that nudge, never to a
 *  confirmed-empty render. Clock starts when the listener is established. */
export const FRIENDS_LIST_TIMEOUT_MS = 10_000;

export interface UseFriendsListResult {
  friends: DashboardPerson[];
  /** Kept for existing consumers (LandingPage's layout gate). `status !== 'loading'`. */
  hydrated: boolean;
  error: Error | null;
  /** Atomic status; prefer this over `hydrated`/`error` for empty-vs-error decisions. */
  status: FriendsListStatus;
  /** True once the current attempt has been loading past FRIENDS_LIST_TIMEOUT_MS
   *  and has not yet reached a terminal result. Superseded by any terminal result. */
  timedOut: boolean;
  /** Re-subscribe: tears down the current listener and starts exactly one new
   *  attempt with a fresh timeout. No-op while loading (unless timed out), so a
   *  healthy in-flight attempt is never torn down. */
  retry: () => void;
  /** Increments per established attempt (mount, uid change, retry). */
  attemptId: number;
}

interface FriendsAttempt {
  status: FriendsListStatus;
  friends: DashboardPerson[];
  error: Error | null;
  /** The uid this attempt's data belongs to. When it differs from the current
   *  uid, the identity changed THIS render and the reset effect has not run yet,
   *  so the derived values below present `loading` — the new identity must never
   *  observe the previous one's data, not even for a frame. */
  uid: string | null;
}

const EMPTY_FRIENDS: DashboardPerson[] = [];
const loadingAttempt = (uid: string | null): FriendsAttempt => ({
  status: 'loading',
  friends: EMPTY_FRIENDS,
  error: null,
  uid,
});

/**
 * Subscribes to `theaWebUser/{uid}/recipient/*` for the signed-in uid and
 * surfaces non-archived recipients in created-at order as `DashboardPerson`s.
 *
 * Skips all subscription work when `uid` is null (anonymous / signed-out)
 * so the homepage's anon path never opens a Firestore listener.
 *
 * State is a single atomic `{ status, friends, error, uid }` (plus a separate
 * `timedOut`) so status transitions commit in one render. The returned values
 * are DERIVED against the current uid: on an identity change the previous
 * attempt is shown as `loading` immediately (render-time), before the reset
 * effect runs — so account A's data/empty-state can never leak onto account B.
 */
export function useFriendsList(
  uid: string | null,
  firestoreOverride?: Firestore,
): UseFriendsListResult {
  const ctxDb = useDb();
  const firestore = firestoreOverride ?? ctxDb;
  const [attempt, setAttempt] = useState<FriendsAttempt>(() => loadingAttempt(null));
  const [timedOut, setTimedOut] = useState(false);
  const [attemptId, setAttemptId] = useState(0);
  // Blocks a second retry from minting a second attempt before the effect for
  // the first has run (rapid double-tap → exactly one new listener). Cleared
  // at the top of every attempt's effect.
  const retryPendingRef = useRef(false);

  useEffect(() => {
    retryPendingRef.current = false;
    if (!uid) {
      setAttempt(loadingAttempt(null));
      setTimedOut(false);
      return;
    }

    let cancelled = false;
    setAttempt(loadingAttempt(uid));
    setTimedOut(false);
    const timer = setTimeout(() => {
      if (!cancelled) setTimedOut(true);
    }, FRIENDS_LIST_TIMEOUT_MS);

    const ref = collection(firestore, ...recipientCollectionPath(uid));
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (cancelled) return;
        clearTimeout(timer); // terminal result — the timeout no longer applies.
        const rows: Array<Recipient & { _docId: string }> = [];
        snap.forEach((d) => {
          const data = d.data() as Recipient;
          if (data.archivedAt) return;
          rows.push({ ...data, _docId: d.id });
        });
        // Stable sort: createdAt ascending. Falls back to doc id for
        // recipients that haven't had a server-side createdAt populated yet.
        rows.sort((a, b) => {
          const at = a.createdAt?.toMillis?.() ?? 0;
          const bt = b.createdAt?.toMillis?.() ?? 0;
          if (at !== bt) return at - bt;
          return a._docId.localeCompare(b._docId);
        });
        const next: DashboardPerson[] = rows.map((r) => ({
          id: r.recipientId ?? r._docId,
          name: r.name,
          // Resolution chain matches the board page so tiles + board
          // header agree (Kate feedback 2026-05-12). See
          // resolveRecipientEmoji.ts.
          emoji: resolveRecipientEmoji(r.emoji, r.relationship, r.isMe),
          relationship: r.relationship,
          currentRecommendationId: r.currentRecommendationId,
        }));
        setAttempt({ status: 'success', friends: next, error: null, uid });
        setTimedOut(false);
      },
      (err) => {
        if (cancelled) return;
        clearTimeout(timer); // terminal result — the timeout no longer applies.
        // status + error commit in ONE update (no intermediate frame where
        // status is success while an error is pending). Keep the prior
        // friends so existing consumers reading `friends` don't lose them.
        setAttempt((prev) => ({ status: 'error', friends: prev.friends, error: err, uid }));
        setTimedOut(false);
      },
    );

    return () => {
      cancelled = true;
      clearTimeout(timer);
      unsub();
    };
  }, [uid, firestore, attemptId]);

  // Derive against the current uid (see FriendsAttempt.uid): a mismatch means
  // the identity just changed and the reset effect hasn't committed yet.
  const matches = attempt.uid === uid;
  const status: FriendsListStatus = matches ? attempt.status : 'loading';
  const friends = matches ? attempt.friends : EMPTY_FRIENDS;
  const error = matches ? attempt.error : null;
  const effectiveTimedOut = matches ? timedOut : false;

  // Snapshot the effective state for the retry guard (retry has [] deps).
  const stateRef = useRef<{ status: FriendsListStatus; timedOut: boolean }>({
    status,
    timedOut: effectiveTimedOut,
  });
  stateRef.current = { status, timedOut: effectiveTimedOut };

  const retry = useCallback(() => {
    if (retryPendingRef.current) return; // a retry is already in flight
    const { status: s, timedOut: t } = stateRef.current;
    // Spec §3b: no-op while a healthy attempt is loading; allowed only from a
    // terminal error or a timed-out (still-loading) attempt.
    if (s !== 'error' && !(s === 'loading' && t)) return;
    retryPendingRef.current = true;
    setAttemptId((n) => n + 1);
  }, []);

  return {
    friends,
    hydrated: status !== 'loading',
    error,
    status,
    timedOut: effectiveTimedOut,
    retry,
    attemptId,
  };
}
